/**
 * The composer — `docs/activity.md` §7.1, §7.5–§7.7 (Board 7, 8 A, 16 B).
 *
 * **Text · Email** with the contact's *Prefers* first and a channel without
 * a number or address absent; the from line; for email **To · Cc · Bcc ·
 * Subject** — Cc and Bcc each several addresses and people in the company
 * (Aaron, Oct 26, `new/address-box.tsx`); the box; camera, paperclip and
 * **Send**. ⌘↵ sends. The draft is
 * kept per conversation in `sessionStorage` until it is sent — a draft, not
 * state. The phone's is one row: camera, a rounded box, and send (grey until
 * there is something to send), with dictation in the box (§11.1).
 */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import {
  EMAIL_MAX_BYTES, MMS_MAX_BYTES, MMS_MAX_FILES, POSTMARK_EMAIL_MAX_BYTES, POSTMARK_LINKED_MAX_BYTES, countText,
  emailFilesWords, emailLinksNote, formatPhone, plainPunctuation, stoppedTextsWords,
  textCountWords, type ConversationThread, type InboxChannel,
} from '@rowted/shared'
import { Dictation } from '../../components/dictation.js'
import { AddressBox, badCopyWords, copiesFrom, copiesPayload, type Copy } from '../new/address-box.js'
import { upload } from '../../lib/api.js'
import { cn } from '../../lib/cn.js'
import { activityRequest } from '../api.js'
import { Glyph } from '../glyphs.js'
import { BUTTON, PLAIN, PRIMARY, Seg, errorWords, readDraft, writeDraft } from './look.js'
import { SignatureUnder } from './signature.js'
import { StoppedWarning, addressesOf, useStoppedAddresses } from './stopped.js'

const CHANNEL_LABEL: Record<InboxChannel, string> = { sms: 'Text', email: 'Email' }

interface Attached { id: string; name: string; contentType: string; size: number }

/** *From (717) 555-0142 · Renee prefers Text* (Board 7), *From tom.reyes@… · your Outlook* (Board 8 A). */
export function fromLine(thread: ConversationThread, channel: InboxChannel | null): string {
  const composer = thread.composer
  const first = thread.contact?.firstName ?? thread.row.name
  const stopped = composer.stopped ? ` · ${stoppedTextsWords(first)}` : ''
  if (channel === 'email') {
    return composer.emailVia === 'outlook'
      ? `From ${composer.emailFrom} · your Outlook${stopped}`
      : `From ${composer.emailFrom}${stopped}`
  }
  if (channel === 'sms') {
    const number = composer.smsFrom ? formatPhone(composer.smsFrom) : 'the company’s number'
    const prefers = thread.contact?.prefers === 'Text' || thread.contact?.prefers === 'Email'
      ? ` · ${first} prefers ${thread.contact.prefers}` : ''
    return `From ${number}${prefers}`
  }
  if (composer.stopped) return stoppedTextsWords(first)
  return `${thread.row.name} has no mobile number or email address yet.`
}

/**
 * The channel a composer opens on: the one asked for, the draft's, the
 * channel the conversation was last on (Board 7 opens on Text, Board 8 A on
 * Email), else the first offered — Prefers first.
 */
function openingChannel(thread: ConversationThread, asked: InboxChannel | null): InboxChannel | null {
  const offered = thread.composer.channels
  const draft = readDraft(thread.id)
  if (asked && offered.includes(asked)) return asked
  if (draft?.channel && offered.includes(draft.channel)) return draft.channel
  if (thread.row.lastChannel && offered.includes(thread.row.lastChannel)) return thread.row.lastChannel
  return offered[0] ?? null
}

function useComposer(thread: ConversationThread, asked: InboxChannel | null, onSent: () => void) {
  const [channel, setChannel] = useState<InboxChannel | null>(() => openingChannel(thread, asked))
  const [body, setBody] = useState(() => readDraft(thread.id)?.body ?? '')
  const [subject, setSubject] = useState(() => readDraft(thread.id)?.subject ?? thread.composer.subject)
  const [cc, setCc] = useState<Copy[]>(() => copiesFrom(readDraft(thread.id)?.cc))
  const [bcc, setBcc] = useState<Copy[]>(() => copiesFrom(readDraft(thread.id)?.bcc))
  const [files, setFiles] = useState<Attached[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* The thread asked for a channel after it opened (a stop card's *Text* / *Email*). */
  useEffect(() => {
    if (asked && thread.composer.channels.includes(asked)) setChannel(asked)
  }, [asked, thread.composer.channels])

  useEffect(() => {
    writeDraft(thread.id, {
      channel: channel ?? undefined, body, subject: subject === thread.composer.subject ? undefined : subject,
      ...(cc.length > 0 ? { cc } : {}), ...(bcc.length > 0 ? { bcc } : {}),
    })
  }, [thread.id, thread.composer.subject, channel, body, subject, cc, bcc])

  const add = async (chosen: FileList | null) => {
    if (!chosen || chosen.length === 0 || !channel) return
    setError(null)
    const list = [...chosen]
    if (channel === 'sms') {
      if (list.some((file) => !file.type.startsWith('image/'))) { setError('A text can carry photos only.'); return }
      if (files.length + list.length > MMS_MAX_FILES) { setError(`Up to ${MMS_MAX_FILES} photos in a text.`); return }
    }
    /* Less while Postmark sends Rowted's email (Aaron, Oct 26) — the server says which. */
    const limit = channel === 'sms' ? MMS_MAX_BYTES : thread.composer.emailMaxBytes ?? EMAIL_MAX_BYTES
    const total = files.reduce((sum, file) => sum + file.size, 0) + list.reduce((sum, file) => sum + file.size, 0)
    if (total > limit) {
      setError(channel === 'sms' ? 'Photos in a text can be 5 MB together.' : emailFilesWords(limit))
      return
    }
    setBusy(true)
    try {
      for (const file of list) {
        const sent = await upload(file, {
          kind: file.type.startsWith('image/') ? 'photo' : 'attachment', orgUnitId: thread.row.orgUnitId,
        })
        setFiles((current) => [...current, { id: sent.id, name: sent.name, contentType: sent.contentType, size: file.size }])
      }
    } catch (cause) {
      setError(errorWords(cause))
    } finally {
      setBusy(false)
    }
  }

  const send = async () => {
    if (!channel || busy || !body.trim()) return
    const bad = channel === 'email' ? badCopyWords(cc, bcc) : null
    if (bad) { setError(bad); return }
    setBusy(true)
    setError(null)
    try {
      const copies = copiesPayload(cc, bcc)
      await activityRequest(`/conversations/${thread.id}/messages`, {
        method: 'POST',
        body: {
          channel,
          body: channel === 'sms' ? plainPunctuation(body) : body,
          ...(channel === 'email' && subject.trim() ? { subject: subject.trim() } : {}),
          ...(channel === 'email' && copies.cc.length > 0 ? { cc: copies.cc } : {}),
          ...(channel === 'email' && copies.bcc.length > 0 ? { bcc: copies.bcc } : {}),
          ...(channel === 'email' && copies.ccPeople.length > 0 ? { ccPeople: copies.ccPeople } : {}),
          ...(channel === 'email' && copies.bccPeople.length > 0 ? { bccPeople: copies.bccPeople } : {}),
          ...(files.length > 0 ? { fileIds: files.map((file) => file.id) } : {}),
        },
      })
      setBody('')
      setCc([])
      setBcc([])
      setFiles([])
      setSubject(thread.composer.subject)
      writeDraft(thread.id, null)
      onSent()
    } catch (cause) {
      setError(errorWords(cause))
    } finally {
      setBusy(false)
    }
  }

  /* Postmark has stopped sending to one of them (Aaron, Oct 26) — a warning, never a refusal. */
  const stopped = useStoppedAddresses(addressesOf(thread.composer.email, cc, bcc),
    channel === 'email' && thread.composer.emailVia === 'rowted')

  /*
   * While Postmark sends, files past what fits under its 10 MB go as download
   * links (Aaron, Oct 27, answer 16) — said before it happens.
   */
  const linksNote = channel === 'email' && thread.composer.emailMaxBytes === POSTMARK_LINKED_MAX_BYTES
    && files.reduce((sum, file) => sum + file.size, 0) > POSTMARK_EMAIL_MAX_BYTES ? emailLinksNote() : null

  return {
    channel, setChannel, body, setBody, subject, setSubject, cc, setCc, bcc, setBcc, files, setFiles, busy, error, add, send,
    stopped, linksNote,
  }
}

/** ⌘↵ / Ctrl↵ sends (§7.1). */
function sendKeys(send: () => void) {
  return (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      send()
    }
  }
}

/** A textarea that grows with what is written, up to a limit. */
function useGrow(value: string, max: number) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const box = ref.current
    if (!box) return
    box.style.height = 'auto'
    box.style.height = `${Math.min(max, box.scrollHeight)}px`
  }, [value, max])
  return ref
}

function FileChips({ files, onRemove }: { files: Attached[]; onRemove: (id: string) => void }) {
  if (files.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1.5 px-3 pb-2">
      {files.map((file) => (
        <span key={file.id} className="inline-flex h-[26px] max-w-[220px] items-center gap-1.5 rounded-lg border border-line bg-surface px-2 text-[12px] font-semibold">
          <span className={cn('inline-flex', file.contentType === 'application/pdf' ? 'text-danger' : 'text-text-muted')}>
            <Glyph name={file.contentType.startsWith('image/') ? 'camera' : 'file'} size={12} />
          </span>
          <span className="truncate">{file.name}</span>
          <button type="button" aria-label={`Remove ${file.name}`} onClick={() => onRemove(file.id)} className="text-text-muted">
            <Glyph name="close" size={11} stroke={2.4} />
          </button>
        </span>
      ))}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 border-b border-line-faint px-3 py-2 text-[12.5px]">
      <span className="w-12 shrink-0 text-text-muted">{label}</span>
      {children}
    </div>
  )
}

export function Composer({
  thread, asked, onSent, autoFocus = false,
}: { thread: ConversationThread; asked: InboxChannel | null; onSent: () => void; autoFocus?: boolean }) {
  /* Sent, Cc and Bcc go back behind their words, as a new message starts. */
  const c = useComposer(thread, asked, () => { setCcOpen(false); setBccOpen(false); onSent() })
  const [ccOpen, setCcOpen] = useState(() => Boolean(readDraft(thread.id)?.cc?.length))
  const [bccOpen, setBccOpen] = useState(() => Boolean(readDraft(thread.id)?.bcc?.length))
  /* Opened by its button, the box takes the typing; restored from a draft, it waits. */
  const [focusCopy, setFocusCopy] = useState<'cc' | 'bcc' | null>(null)
  const camera = useRef<HTMLInputElement>(null)
  const clip = useRef<HTMLInputElement>(null)
  const box = useGrow(c.body, 220)
  const count = c.channel === 'sms' && c.body ? textCountWords(countText(c.body)) : null

  useEffect(() => { if (autoFocus) box.current?.focus({ preventScroll: true }) }, [autoFocus, thread.id, box])

  return (
    <div className="rounded-xl border border-line bg-surface focus-within:border-accent focus-within:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]">
      <div className="flex items-center gap-2 border-b border-line-faint px-2.5 py-2">
        {thread.composer.channels.length > 0 && c.channel && (
          <Seg label="Send as" height={22} value={c.channel} onChange={(next) => { c.setChannel(next); c.setFiles([]) }}
            options={thread.composer.channels.map((value) => ({ value, label: CHANNEL_LABEL[value] }))} />
        )}
        <span className="truncate text-[11.5px] text-text-muted">{fromLine(thread, c.channel)}</span>
      </div>
      {c.channel === 'email' && (
        <>
          <Row label="To">
            <b className="truncate font-semibold">{thread.composer.email}</b>
            <span className="flex-1" />
            {!ccOpen && (
              <button type="button" className="text-text-muted hover:text-text"
                onClick={() => { setCcOpen(true); setFocusCopy('cc') }}>Cc</button>
            )}
            {!bccOpen && (
              <button type="button" className="text-text-muted hover:text-text"
                onClick={() => { setBccOpen(true); setFocusCopy('bcc') }}>Bcc</button>
            )}
          </Row>
          {ccOpen && (
            <Row label="Cc">
              <AddressBox label="Cc" look="row" phone={false} value={c.cc} onChange={c.setCc} taken={c.bcc}
                autoFocus={focusCopy === 'cc'} />
            </Row>
          )}
          {bccOpen && (
            <Row label="Bcc">
              <AddressBox label="Bcc" look="row" phone={false} value={c.bcc} onChange={c.setBcc} taken={c.cc}
                autoFocus={focusCopy === 'bcc'} />
            </Row>
          )}
          <Row label="Subject">
            <input value={c.subject} onChange={(event) => c.setSubject(event.target.value)} aria-label="Subject"
              className="min-w-0 flex-1 bg-transparent outline-none focus-visible:shadow-none" />
          </Row>
          <StoppedWarning stopped={c.stopped} to={thread.composer.email} className="px-3 pt-2" />
        </>
      )}
      <textarea
        ref={box}
        value={c.body}
        rows={1}
        disabled={!c.channel}
        aria-label={c.channel === 'email' ? 'Email' : 'Text'}
        onChange={(event) => c.setBody(event.target.value)}
        onKeyDown={sendKeys(() => void c.send())}
        className={cn('block w-full resize-none bg-transparent px-3 py-2.5 text-[13px] leading-[1.45] text-text outline-none focus-visible:shadow-none',
          /* Boards 7 and 8 A: 64 and 44 of words inside 10 px of padding — 84 and 64 drawn. */
          c.channel === 'email' ? 'min-h-[64px]' : 'min-h-[84px]')}
      />
      <FileChips files={c.files} onRemove={(id) => c.setFiles((current) => current.filter((file) => file.id !== id))} />
      {c.linksNote && <div data-links-note className="px-3 pb-2 text-[11.5px] text-text-muted">{c.linksNote}</div>}
      {/* What the email will end with (Aaron, Oct 24, note 2.7) — added as it leaves, never stored. */}
      {c.channel === 'email' && <SignatureUnder folded className="px-3 pb-2" />}
      {c.error && <div className="px-3 pb-2 text-[12px] text-danger">{c.error}</div>}
      <div className="flex items-center gap-1.5 border-t border-line-faint px-2.5 py-2">
        <button type="button" aria-label="Add a photo" disabled={!c.channel || c.busy} onClick={() => camera.current?.click()}
          className={cn(BUTTON, PLAIN, 'h-[30px] w-[30px] text-[13px]')}>
          <Glyph name="camera" size={15} />
        </button>
        <button type="button" aria-label="Attach a file" disabled={!c.channel || c.busy} onClick={() => clip.current?.click()}
          className={cn(BUTTON, PLAIN, 'h-[30px] w-[30px] text-[13px]')}>
          <Glyph name="paperclip" size={15} />
        </button>
        <input ref={camera} type="file" accept="image/*" capture="environment" multiple hidden
          onChange={(event) => { void c.add(event.target.files); event.target.value = '' }} />
        <input ref={clip} type="file" accept={c.channel === 'sms' ? 'image/*' : undefined} multiple hidden
          onChange={(event) => { void c.add(event.target.files); event.target.value = '' }} />
        <span className="flex-1" />
        {count && <span className="whitespace-nowrap text-[11px] text-text-faint">{count}</span>}
        <button type="button" onClick={() => void c.send()} disabled={!c.channel || c.busy || !c.body.trim()}
          className={cn(BUTTON, PRIMARY, 'h-8 px-3.5 text-[12.5px]')}>
          <Glyph name="send" size={14.5} />Send
        </button>
      </div>
    </div>
  )
}

/** The phone's composer (Board 16 B): camera, *Text Renee…*, send — grey until there is text. */
export function PhoneComposer({
  thread, asked, onSent, channel: forced,
}: { thread: ConversationThread; asked: InboxChannel | null; onSent: () => void; channel?: InboxChannel | null }) {
  const c = useComposer(thread, asked, () => { setCopies({ cc: false, bcc: false, focus: null }); onSent() })
  const camera = useRef<HTMLInputElement>(null)
  const box = useGrow(c.body, 140)
  const [copies, setCopies] = useState<{ cc: boolean; bcc: boolean; focus: 'cc' | 'bcc' | null }>(() => ({
    cc: Boolean(readDraft(thread.id)?.cc?.length), bcc: Boolean(readDraft(thread.id)?.bcc?.length), focus: null,
  }))
  const first = thread.contact?.firstName ?? thread.row.name
  const ready = Boolean(c.channel && c.body.trim()) && !c.busy

  const { setChannel } = c
  useEffect(() => { if (forced && thread.composer.channels.includes(forced)) setChannel(forced) },
    [forced, setChannel, thread.composer.channels])

  if (!c.channel) {
    return (
      <div className="border-t border-line bg-surface px-4 pb-4 pt-3 text-[13px] text-text-muted">{fromLine(thread, null)}</div>
    )
  }
  return (
    <div className="shrink-0 border-t border-line bg-surface">
      {/*
        * An email on a phone (Aaron, Oct 26): who it goes to, and Cc and Bcc
        * behind their own words until they are wanted — a text's row is as
        * Board 16 B draws it.
        */}
      {c.channel === 'email' && (
        <div className="flex flex-col gap-1.5 px-3 pt-1.5">
          <div className="flex items-center gap-1">
            <span className="min-w-0 flex-1 truncate text-[12.5px] text-text-muted">To {thread.composer.email}</span>
            {!copies.cc && (
              <button type="button" onClick={() => setCopies((was) => ({ ...was, cc: true, focus: 'cc' }))}
                className="h-11 px-2.5 text-[14px] font-semibold text-accent">Cc</button>
            )}
            {!copies.bcc && (
              <button type="button" onClick={() => setCopies((was) => ({ ...was, bcc: true, focus: 'bcc' }))}
                className="h-11 px-2.5 text-[14px] font-semibold text-accent">Bcc</button>
            )}
          </div>
          {copies.cc && (
            <div className="flex items-start gap-2">
              <span className="w-9 shrink-0 pt-[13px] text-[13px] text-text-muted">Cc</span>
              <AddressBox label="Cc" phone value={c.cc} onChange={c.setCc} taken={c.bcc} autoFocus={copies.focus === 'cc'} />
            </div>
          )}
          {copies.bcc && (
            <div className="flex items-start gap-2">
              <span className="w-9 shrink-0 pt-[13px] text-[13px] text-text-muted">Bcc</span>
              <AddressBox label="Bcc" phone value={c.bcc} onChange={c.setBcc} taken={c.cc} autoFocus={copies.focus === 'bcc'} />
            </div>
          )}
          <StoppedWarning stopped={c.stopped} to={thread.composer.email} />
        </div>
      )}
      <FileChips files={c.files} onRemove={(id) => c.setFiles((current) => current.filter((file) => file.id !== id))} />
      {c.linksNote && <div data-links-note className="px-3 pt-1 text-[12px] text-text-muted">{c.linksNote}</div>}
      {c.error && <div className="px-3 pt-2 text-[12px] text-danger">{c.error}</div>}
      <div className="flex items-end gap-2 px-2.5 pb-4 pt-2.5">
        <button type="button" aria-label="Add a photo" onClick={() => camera.current?.click()}
          className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full text-text-muted">
          <Glyph name="camera" size={20} />
        </button>
        <input ref={camera} type="file" accept="image/*" multiple hidden
          onChange={(event) => { void c.add(event.target.files); event.target.value = '' }} />
        <div className="flex min-h-10 flex-1 items-center gap-1.5 rounded-[20px] border border-line py-[7px] pl-3.5 pr-1.5">
          <textarea
            ref={box}
            rows={1}
            value={c.body}
            aria-label={c.channel === 'email' ? 'Email' : 'Text'}
            placeholder={`${c.channel === 'email' ? 'Email' : 'Text'} ${first}…`}
            onChange={(event) => c.setBody(event.target.value)}
            onKeyDown={sendKeys(() => void c.send())}
            className="block min-w-0 flex-1 resize-none bg-transparent text-[14.5px] leading-[1.3] text-text outline-none placeholder:text-text-faint focus-visible:shadow-none"
          />
          <Dictation variant="icon" onText={(said) => c.setBody((was) => (was.trim() ? `${was.trimEnd()} ${said}` : said))} />
        </div>
        <button type="button" aria-label="Send" disabled={!ready} onClick={() => void c.send()}
          className={cn('inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full',
            ready ? 'bg-accent text-white' : 'bg-line-soft text-text-faint')}>
          <Glyph name="send" size={17} stroke={2.1} />
        </button>
      </div>
    </div>
  )
}
